import type { Dispatch, SetStateAction } from "react";
import { useState } from "react";

import classNames from "classnames";
import dompurify from "isomorphic-dompurify";

import { useLocale } from "@jti/app-context/useLocale";
import { DeviceVideoIcon } from "@jti/brand-icons/device-video";
import { LockFullCloseIcon } from "@jti/brand-icons/lock-full-close";
import { UiArticleIcon } from "@jti/brand-icons/ui-article";
import { UiCheckIcon } from "@jti/brand-icons/ui-check";
import { UiQuizIcon } from "@jti/brand-icons/ui-quiz";
import { PictureBehaviour } from "@jti/core-components/components/PictureBehaviour";
import type { BlogItemImage, BlogItemVideo } from "@jti/ui/components/BlogListing/BlogItem";
import { TierBadge } from "@jti/ui/components/TierBadge";
import { Typography } from "@jti/ui/components/Typography";
import { videoPlayerContainerPropsToVideoPlayerProps } from "@jti/ui/components/VideoContainer/VideoContainer";
import { useTranslations } from "@jti/ui/providers/useTranslations";
import { formatDate } from "@jti/ui/utils/formatDate/formatDate";

import { LoyaltyVideoPlayer } from "../../LoyaltyVideoPlayer";
import { ArticleRestrictedContentResponsiveContainer } from "../ArticleRestrictedResponsiveContainer";

import styles from "./ArticleRestrictedContentHeader.module.scss";

export interface ArticleRestrictedContentHeadingProps {
  title: string;
  subtitle: string;
  tier?: string;
  creationDate: string;
  isRead: boolean | null;
  isWatched?: boolean | null;
  isCompleted?: boolean | null;
  points: number | null;
  image: BlogItemImage | null;
  isAvailable: boolean | null;
  controlColor?: "dark" | "light";
  disableTiers?: boolean;
  video?: BlogItemVideo | null;
  articleType?: "article" | "video" | "quiz";
  articleId?: string;
  isLogged: boolean;
  percentageToWatch?: number;
  headerMediaType?: "image" | "video";
}

const { sanitize } = dompurify;

export const ArticleRestrictedContentHeader = ({
  title,
  subtitle,
  creationDate,
  isRead,
  isWatched,
  isCompleted,
  points,
  tier,
  image,
  controlColor = "dark",
  isAvailable,
  disableTiers,
  video,
  articleType,
  articleId,
  isLogged,
  percentageToWatch,
  headerMediaType,
}: ArticleRestrictedContentHeadingProps) => {
  const { __ } = useTranslations();
  // Pass the SSR-consistent locale so the server doesn't format the date as en-US (getLocale()
  // defaults to en-US on the server), which caused a hydration mismatch and a full-page re-render.
  const locale = useLocale();
  const isTypeOfArticle = articleType === "article" || !articleType;
  const isTypeOfVideo = articleType === "video";
  const isTypeOfQuiz = articleType === "quiz";
  const isVideoExpected = isTypeOfVideo || headerMediaType === "video";
  const showVideo = isVideoExpected && video;
  const showImage =
    !isVideoExpected &&
    (isTypeOfArticle || isTypeOfQuiz) &&
    image &&
    Boolean(image.smallReference || image.mediumReference || image.largeReference);
  const ribbonColor = showVideo ? video?.controlColor : controlColor;
  const themeClass = ribbonColor === "light" ? "dark-theme" : "light-theme";
  const showVideoRestricted = isVideoExpected && !(isLogged && (isAvailable || disableTiers));
  const showPointsBlock = isLogged && !!points;
  const shouldTrackVideoWatch = !isWatched && isTypeOfVideo;
  const [isWatchedState, setIsWatchedState] = useState<boolean>(isWatched ?? false);
  const [isVideoPlaying, setIsVideoPlaying] = useState<boolean>(false);
  const [isVideoPlayed, setIsVideoPlayed] = useState<boolean>(false);
  const isPointsEarned =
    (isTypeOfVideo && isWatchedState) || (isTypeOfArticle && isRead) || (isTypeOfQuiz && isCompleted);

  const handleVideoWatched = () => {
    setIsWatchedState(true);
  };

  const handleIsPlayingChange: Dispatch<SetStateAction<boolean>> = (value) => {
    setIsVideoPlaying(value);
    const resolved = typeof value === "function" ? value(isVideoPlaying) : value;
    if (resolved) {
      setIsVideoPlayed(true);
    }
  };

  const videoProps = showVideo ? videoPlayerContainerPropsToVideoPlayerProps(video) : null;

  return (
    <div className={styles.header}>
      <ArticleRestrictedContentResponsiveContainer>
        <div className={styles.textWrapper}>
          <h5 className={styles.subtitle} dangerouslySetInnerHTML={{ __html: sanitize(subtitle) }} />
          <Typography variant="h2" className={styles.title}>
            {title}
          </Typography>
          <div data-testid="creationDate" className={classNames(styles.creationDateText, styles.creationDate)}>
            {formatDate(creationDate, undefined, locale)}
          </div>

          <div className={styles.tierBadge}>
            <TierBadge
              text={__("loyalty.tierBadge.text", [tier || ""])}
              tier={disableTiers ? undefined : tier}
              isAvailable={isAvailable ?? false}
            />
          </div>
        </div>
      </ArticleRestrictedContentResponsiveContainer>

      <div className={styles.imageWrapper}>
        {showImage && (
          <PictureBehaviour
            sources={[
              { srcSet: image?.smallReference, media: "(max-width: 959px)" },
              { srcSet: image?.mediumReference, media: "(min-width: 960px) and (max-width: 1919px)" },
            ]}
            imageProps={{
              className: styles.image,
              src: image?.largeReference,
              alt: image?.altText,
              "data-testid": "articleRestrictedContentHeaderImage",
            }}
          />
        )}
        {showVideo && videoProps && (
          <LoyaltyVideoPlayer
            {...videoProps}
            isMainPlayButtonAllowed={!showVideoRestricted}
            showControls={isVideoPlayed}
            disableSeek={true}
            onWatchTimeReached={handleVideoWatched}
            isWatchTimeTracked={shouldTrackVideoWatch}
            videoId={articleId ?? ""}
            percentageToWatch={percentageToWatch}
            onPlayingChange={handleIsPlayingChange}
            pointsGranted={points ?? undefined}
          >
            {!showVideoRestricted && !isVideoPlaying && (isVideoPlayed || !videoProps.hostedExternally) && (
              <div className={styles.videoOverlay}></div>
            )}

            {showVideoRestricted && (
              <div className={styles.videoLockedWrapper} data-testid="articleRestrictedContentHeaderVideoLockedIcon">
                <LockFullCloseIcon height={50} width={50} />
              </div>
            )}
          </LoyaltyVideoPlayer>
        )}
        {showPointsBlock && (
          <span
            data-testid="articleRestrictedContentHeaderPointsBlock"
            className={classNames(styles.earnPointsBlock, themeClass)}
          >
            {isPointsEarned ? (
              <UiCheckIcon height={20} width={20} />
            ) : (
              <>
                {isTypeOfVideo && <DeviceVideoIcon height={20} width={20} />}
                {isTypeOfArticle && <UiArticleIcon height={20} width={20} />}
                {isTypeOfQuiz && <UiQuizIcon height={20} width={20} />}
              </>
            )}
            <Typography variant="body2" weight="medium" className={styles.earnPointsBlockText}>
              {__(`loyalty.articleRestrictedContentHeader.${isPointsEarned ? "pointsEarned" : "earnPoints"}`, [points])}
            </Typography>
          </span>
        )}
      </div>
    </div>
  );
};
